import {
  alarmToColor,
  alarmToDisplayname,
  alarmToType,
} from '../../../utils/AlarmUtils';
import { GetCriteriaNameFn } from '../../Monitor/hooks/useCriteriaNames';
import { SBLayout, SBPieData } from '../graphTypes';

export type HealthStatsType = {
  ts: string;
  location_count: number;
  environment_category_location_count: number;
  stability_category_location_count: number;
  network_speed_category_location_count: number;
  internet_connection_category_location_count: number;
  device_qoe_category_location_count: number;
  pod_qoe_category_location_count: number;
  wifi_coverage_category_location_count: number;
  offline_network_location_count: number;
  csp_outage_location_count: number;
  too_many_optimizations_location_count: number;
  failed_optimizations_location_count: number;
  device_qoe_location_count: number;
  pod_qoe_location_count: number;
  download_volatile_location_count: number;
  download_low_location_count: number;
  offline_pod_location_count: number;
  evil_twin_location_count: number;
  parallel_network_location_count: number;
  pod_placement_location_count: number;
  coverage_location_count: number;
  thermal_reboot_location_count: number;
  non_thermal_reboot_location_count: number;
};
/* PROCESSORS */

export type breakdownType = {
  id: string;
  name: string;
  queryString?: string;
  count: number;
  fill?: string;
  displayName?: string;
  type: string;
  value?: number;
};

export const makeHealthBreakdown = (
  breakdown: HealthStatsType,
  locationCount: number,
  getCriteriaName: GetCriteriaNameFn,
): SBPieData[] => {
  if (!breakdown) {
    return [];
  }

  const keys = Object.keys(breakdown).filter(
    (key: string) => !key.includes('category'),
  );
  const categoryKeys = Object.keys(breakdown).filter((key: string) =>
    key.includes('category'),
  );

  const pieData: SBPieData[] = [];

  const metrics: breakdownType[] = [];
  const categories: breakdownType[] = [];

  // Category data
  categoryKeys?.forEach((key: string) => {
    const keyId = key.split('_category_location_count')[0];
    // @ts-ignore
    const breakdownCount: number = parseFloat(breakdown[key]);
    categories.push({
      id: keyId,
      name: keyId,
      queryString: key,
      count: breakdownCount * locationCount,
      fill: alarmToColor(keyId),
      displayName: alarmToDisplayname(keyId, getCriteriaName),
      type: 'category',
    });
  });

  // Metric data
  keys?.forEach((key: string) => {
    const keyId = key.split('_location_count')[0];
    if (keyId !== 'ts' && keyId !== 'location_count') {
      // @ts-ignore
      const breakdownCount: number = parseFloat(breakdown[key]);
      metrics.push({
        id: keyId,
        name: keyId,
        queryString: key,
        count: breakdownCount * locationCount,
        fill: alarmToColor(keyId),
        displayName: alarmToDisplayname(keyId, getCriteriaName),
        type: alarmToType(keyId),
      });
    }
  });

  // Inner pie chart circle
  categories.forEach((category: breakdownType) => {
    pieData.push({
      id: category.id,
      name: alarmToDisplayname(category.id, getCriteriaName),
      count: category.count,
      locationCount: locationCount,
      fill: alarmToColor(category.id),
      children: [],
    });
  });

  // Outer pie chart circle
  metrics.forEach((metric: breakdownType) => {
    if (metric.type !== 'location') {
      const found = pieData.find((cat: any) => cat.id === metric.type);
      if (found && found.children) {
        found.children.push({
          id: metric.id,
          fill: metric.fill,
          name:
            metric.displayName ||
            alarmToDisplayname(metric.id, getCriteriaName),
          count: metric.count,
          locationCount: locationCount,
        });
      }
    }
  });

  /* Calculate percentages */
  pieData.forEach((dat: SBPieData) => {
    dat.children?.forEach((child: any) => {
      if (child.count && dat.count) {
        child.value = child.count / dat.count;
      }
    });
    dat.value = dat.count;
  });

  const sortedPie = pieData.sort((a: SBPieData, b: SBPieData) => {
    if (a.value && b.value) {
      return a.value < b.value ? 1 : a.value > b.value ? -1 : 0;
    }
    return 0;
  });

  return sortedPie;
};

export const getIssuesFromBreakdown = (bd: SBPieData[]): SBPieData[] => {
  const bdArr: any = [];
  bd.forEach((alarm: any) => {
    alarm.children.forEach((child: SBPieData) => {
      child.parent = alarm.id;
      bdArr.push(child);
    });
  });
  return bdArr;
};

// Health summary
export const HealthScorePieLayout: SBLayout = {
  slices: [
    {
      insideRadius: 0,
      outsideRadius: 0.12,
      label: false,
      hoverLabel: true,
    },
    {
      insideRadius: 0.13,
      outsideRadius: 0.17,
      label: false,
      hoverLabel: true,
    },
  ],
  animSpeed: 5,
  animStep: 5,
};
